import { useState, useRef, useMemo, useEffect } from 'react';
import CloseIcon from '../../../assets/icons/close.svg?react';
import SearchBox from '../../../components/SearchBox/SearchBox';
import ResetIcon from '../../../assets/icons/reset.svg?react';
import useStationsSimple from '../../../hooks/useStationsSimple';
import { SkeletonTable } from '../../../components/Skeleton';
import ChevronDownIcon from '../../../assets/icons/chevron-down.svg?react';
import Pagination from '../../../components/Pagination/Pagination';
import { useAddStationsToDrResource, useRemoveStationsFromDrResource } from '../../../hooks/queries/resourceRelationQueries';

const ASSIGNED_LIST_PAGE_SIZE = 10;

const tdTop = { verticalAlign: 'top' };
const breakAll = { wordBreak: 'break-all' };

function parsePowerMeterContractParts(value) {
  if (value == null || String(value).trim() === '') return [];
  return String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function PowerMeterContractTd({ value }) {
  const parts = parsePowerMeterContractParts(value);
  return (
    <td style={tdTop}>
      {parts.length === 0 ? (
        '-'
      ) : parts.length === 1 ? (
        <span style={breakAll}>{parts[0]}</span>
      ) : (
        <div className="flex flex-col gap-2">
          {parts.map((p, i) => (
            <span key={`${p}-${i}`} style={breakAll}>
              {p}
            </span>
          ))}
        </div>
      )}
    </td>
  );
}

const StationAssignSideModal = ({ onClose, drResource, onAssign }) => {
  const [searchValue, setSearchValue] = useState('');
  const [canSearch, setCanSearch] = useState(true);
  const searchBoxRef = useRef(null);
  const assignedSearchBoxRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [unassignedSelected, setUnassignedSelected] = useState([]);
  const [assignedSelected, setAssignedSelected] = useState([]);
  const [assignedStations, setAssignedStations] = useState([]);
  const [assignedFilterText, setAssignedFilterText] = useState('');
  const [assignedListPage, setAssignedListPage] = useState(1);

  // API 훅들
  const addStationsMutation = useAddStationsToDrResource();
  const removeStationsMutation = useRemoveStationsFromDrResource();

  // 현재 DR자원에 할당된 충전소 ID 목록
  const assignedStationIds = useMemo(() => {
    return drResource?.Stations?.map(station => station.StationSysId) || [];
  }, [drResource]);

  // 초기 할당된 충전소 데이터 설정
  const initialAssignedStations = useMemo(() => {
    return drResource?.Stations || [];
  }, [drResource]);

  // 초기 데이터와 현재 데이터 비교하여 변경사항 감지
  const hasChanges = useMemo(() => {
    if (assignedStations.length !== initialAssignedStations.length) {
      return true;
    }
    
    const currentIds = assignedStations.map(s => s.StationSysId).sort();
    const initialIds = initialAssignedStations.map(s => s.StationSysId).sort();
    
    return JSON.stringify(currentIds) !== JSON.stringify(initialIds);
  }, [assignedStations, initialAssignedStations]);

  // 미할당 충전소 조회 (DrResourceSysId가 null이거나 undefined인 충전소)
  const { stations, isLoading, error, totalCount } = useStationsSimple({
    Keyword: searchValue,
    page: currentPage,
    pageSize: pageSize,
  });

  // 미할당 충전소만 필터링 (현재 assignedStations에 없는 충전소)
  const unassignedStations = useMemo(() => {
    const currentAssignedIds = assignedStations.map(s => s.StationSysId);
    return stations.filter(station => 
      !station.DrResourceSysId && !currentAssignedIds.includes(station.StationSysId)
    );
  }, [stations, assignedStations]);

  const assignedFilteredStations = useMemo(() => {
    const q = assignedFilterText.trim().toLowerCase();
    if (!q) return assignedStations;
    return assignedStations.filter((s) => {
      const name = (s.CpoStationName || '').toLowerCase();
      const id = (s.PowerMeterContractNumber || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [assignedStations, assignedFilterText]);

  const assignedListTotalPages = Math.max(
    1,
    Math.ceil(assignedFilteredStations.length / ASSIGNED_LIST_PAGE_SIZE)
  );

  const assignedPaginatedStations = useMemo(() => {
    const start = (assignedListPage - 1) * ASSIGNED_LIST_PAGE_SIZE;
    return assignedFilteredStations.slice(start, start + ASSIGNED_LIST_PAGE_SIZE);
  }, [assignedFilteredStations, assignedListPage]);

  useEffect(() => {
    if (assignedListPage > assignedListTotalPages) {
      setAssignedListPage(assignedListTotalPages);
    }
  }, [assignedListPage, assignedListTotalPages]);

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(1); // 검색 시 첫 페이지로 리셋
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleReset = () => {
    if (searchBoxRef.current) {
      searchBoxRef.current.reset();
    }
    setSearchValue('');
    setCanSearch(true);
    setCurrentPage(1);
  };

  const handleAssignedSearch = (value) => {
    setAssignedFilterText(String(value ?? '').trim());
    setAssignedListPage(1);
  };

  const handleAssignedReset = () => {
    if (assignedSearchBoxRef.current) {
      assignedSearchBoxRef.current.reset();
    }
    setAssignedFilterText('');
    setAssignedListPage(1);
  };

  const handleUnassignedSelect = (station) => {
    setUnassignedSelected(prev => {
      const isSelected = prev.some(s => s.StationSysId === station.StationSysId);
      if (isSelected) {
        return prev.filter(s => s.StationSysId !== station.StationSysId);
      } else {
        return [...prev, station];
      }
    });
  };

  const handleAssignedSelect = (station) => {
    setAssignedSelected(prev => {
      const isSelected = prev.some(s => s.StationSysId === station.StationSysId);
      if (isSelected) {
        return prev.filter(s => s.StationSysId !== station.StationSysId);
      } else {
        return [...prev, station];
      }
    });
  };

  // 추가할 충전소 -> 추가된 충전소로 이동
  const handleAddToAssigned = () => {
    if (unassignedSelected.length === 0) return;
    
    setAssignedStations(prev => [...prev, ...unassignedSelected]);
    setUnassignedSelected([]);
  };

  // 추가된 충전소 -> 추가할 충전소로 이동
  const handleRemoveFromAssigned = () => {
    if (assignedSelected.length === 0) return;
    
    setAssignedStations(prev => 
      prev.filter(station => !assignedSelected.some(s => s.StationSysId === station.StationSysId))
    );
    setAssignedSelected([]);
  };

  const handleAssign = async () => {
    if (!hasChanges || !drResource?.DrResourceSysId) return;
    
    try {
      // 제거할 충전소들 찾기
      const stationsToRemove = initialAssignedStations.filter(
        initialStation => !assignedStations.some(
          currentStation => currentStation.StationSysId === initialStation.StationSysId
        )
      );
      
      // 추가할 충전소들 찾기
      const stationsToAdd = assignedStations.filter(
        currentStation => !initialAssignedStations.some(
          initialStation => initialStation.StationSysId === currentStation.StationSysId
        )
      );

      // 제거 API 호출
      if (stationsToRemove.length > 0) {
        const removeStationIds = stationsToRemove.map(station => station.StationSysId);
        await removeStationsMutation.mutateAsync({
          drResourceSysId: drResource.DrResourceSysId,
          stationSysIds: removeStationIds
        });
      }

      // 추가 API 호출
      if (stationsToAdd.length > 0) {
        const addStationIds = stationsToAdd.map(station => station.StationSysId);
        await addStationsMutation.mutateAsync({
          drResourceSysId: drResource.DrResourceSysId,
          stationSysIds: addStationIds
        });
      }

      // 성공 시 콜백 호출 및 모달 닫기
      onAssign(assignedStations);
      onClose();
      
    } catch (error) {
      console.error('충전소 할당 실패:', error);
      alert('충전소 할당에 실패했습니다. 다시 시도해주세요.');
    }
  };

  // 초기 데이터 설정
  useEffect(() => {
    setAssignedStations(initialAssignedStations);
  }, [initialAssignedStations]);

  return (
    <div className="side-modal-background">
      <div className="side-modal-container">
        <div className="side-modal-header">
          <h3>DR자원 충전소 할당 편집</h3>
          <div className="side-modal-close" onClick={onClose}>
            <CloseIcon />
          </div>
        </div>
        
        <div className="side-modal-content">
          {/* DR자원 정보 */}
          <div className="mb-20">
            <h4 className="text-h2 text-primary mb-5">{drResource?.ResourceName || 'DR자원 이름 없음'}</h4>
            <p className="text-b1 text-gray-600">DR자원에 충전소를 할당하거나 할당을 해제할 수 있습니다.</p>
          </div>

          {/* 검색 영역 */}
          <div className="flex gap-4 mb-20 w-full">
            <div className="flex-1">  
              <SearchBox
                ref={searchBoxRef}
                placeholder="미할당 충전소 이름 또는 계량기 계약번호 검색"
                onSearch={handleSearch}
                showFilter={false}
                onSearchStateChange={setCanSearch}
                defaultValue={searchValue}
                selectedFilterRef={null}
                searchValueRef={null}
              />
            </div>
            <button className="btn big-btn btn-primary" onClick={() => handleSearch(searchValue)}>
              검색
            </button>
          </div>

          {/* 추가할 충전소 섹션 */}
          <div className="mb-20">
            <h5 className="text-b2-700 mb-10 text-gray-700">미할당 충전소</h5>
            <div className="primary-table">
              <table>
                {isLoading && (
                  <SkeletonTable rows={5} columns={3} />
                )}
                {!isLoading && !error && (
                  <>
                    <thead>
                      <tr>
                        <th width="30px"></th>
                        <th>충전소 이름</th>
                        <th>계량기 계약번호</th>
                      </tr>
                    </thead>
                    <tbody>
                      {error && (
                        <tr>
                          <td colSpan="3" className="no-data">
                            충전소 데이터를 불러오는데 실패했습니다.
                          </td>
                        </tr>
                      )}
                      {!isLoading && !error && unassignedStations.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="no-data">
                            {searchValue ? '검색결과 없음' : '미할당 충전소가 없습니다.'}
                          </td>
                        </tr>
                      ) : (
                        !isLoading && !error && unassignedStations.map((station) => {
                          const isSelected = unassignedSelected.some(s => s.StationSysId === station.StationSysId);
                          return (
                            <tr key={station.StationSysId}>
                              <td width="30px" style={tdTop}>
                                <input 
                                  type="checkbox" 
                                  checked={isSelected}
                                  onChange={() => handleUnassignedSelect(station)} 
                                />
                              </td>
                              <td style={tdTop}>{station.CpoStationName || '충전소 이름 없음'}</td>
                              <PowerMeterContractTd value={station.PowerMeterContractNumber} />
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </>
                )}
              </table>
            </div>
            <div className="pagination-wrapper">
              <Pagination
                currentPage={currentPage}
                totalPages={Math.max(1, Math.ceil(totalCount / pageSize))}
                onPageChange={handlePageChange}
                size="small"
              />
            </div>
          </div>
          {/* 아래 화살표 버튼 */}
          <div className="flex justify-center gap-5 mt-10">
            <button 
              className="btn small-btn btn-style-5"
              onClick={handleAddToAssigned}
              disabled={unassignedSelected.length === 0}
            >
              <ChevronDownIcon />
            </button>
            <button 
                className="btn small-btn btn-style-5"
                onClick={handleRemoveFromAssigned}
                disabled={assignedSelected.length === 0}
              >
                <ChevronDownIcon style={{ transform: 'rotate(180deg)' }}/>
              </button>
            </div>

          {/* 할당된 충전소 섹션 */}
          <div className="mb-20">
            <h5 className="text-b2-700 mb-10 text-gray-700">할당된 충전소</h5>
            <div className="flex gap-4 mb-10 w-full">
              <div className="flex-1">
                <SearchBox
                  ref={assignedSearchBoxRef}
                  placeholder="할당된 충전소 이름 또는 계량기 계약번호 검색"
                  onSearch={handleAssignedSearch}
                  showFilter={false}
                  onSearchStateChange={() => {}}
                  defaultValue={assignedFilterText}
                  selectedFilterRef={null}
                  searchValueRef={null}
                />
              </div>
              <button
                type="button"
                className="btn big-btn btn-primary"
                onClick={() => handleAssignedSearch(assignedSearchBoxRef.current?.getValue?.())}
              >
                검색
              </button>
            </div>
            <div className="primary-table">
              <table>
                <thead>
                  <tr>
                    <th width="30px"></th>
                    <th>충전소 이름</th>
                    <th>계량기 계약번호</th>
                  </tr>
                </thead>
                <tbody>
                  {assignedStations.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="no-data">
                        할당된 충전소가 없습니다.
                      </td>
                    </tr>
                  ) : assignedFilteredStations.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="no-data">
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    assignedPaginatedStations.map((station) => {
                      const isSelected = assignedSelected.some(s => s.StationSysId === station.StationSysId);
                      return (
                        <tr key={station.StationSysId}>
                          <td width="30px" style={tdTop}>
                            <input 
                              type="checkbox" 
                              checked={isSelected}
                              onChange={() => handleAssignedSelect(station)} 
                            />
                          </td>
                          <td style={tdTop}>{station.CpoStationName || '충전소 이름 없음'}</td>
                          <PowerMeterContractTd value={station.PowerMeterContractNumber} />
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {assignedFilteredStations.length > 0 && (
              <div className="pagination-wrapper">
                <Pagination
                  currentPage={assignedListPage}
                  totalPages={assignedListTotalPages}
                  onPageChange={setAssignedListPage}
                  size="small"
                />
              </div>
            )}
          </div>

          {/* 하단 버튼 */}
          <div className="flex justify-end">
            <button 
              className={`btn big-btn w-full ${hasChanges ? 'btn-primary' : 'btn-style-5'}`}
              onClick={handleAssign}
              disabled={!hasChanges || addStationsMutation.isPending || removeStationsMutation.isPending}
            >
              {addStationsMutation.isPending || removeStationsMutation.isPending ? '할당 중...' : '충전소 할당하기'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StationAssignSideModal;
